import type { onboardingApiSpec } from "@wingspanhq/wingspan-api-types";
import { getTaxIdLabel } from "../../../shared/utils/taxId";
import { AccountType } from "../steps/CreateAccount/AccountType";
import { BusinessInformation } from "../steps/CreateAccount/BusinessInformation";
import { PersonalInformation } from "../steps/CreateAccount/PersonalInformation";
import { Representative } from "../steps/CreateAccount/Representative";
import { Review } from "../steps/CreateAccount/Review";
import { SecureAccount } from "../steps/CreateAccount/SecureAccount";
import { OnboardingContext, OnboardingModuleCreateAccount } from "../types";
import { CreateAccountContext } from "../steps/CreateAccount/types";
import { InternationalUSPersonInformation } from "../steps/CreateAccount/InternationalUSPersonInformation";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useQueryVerifications } from "../../../query/onboarding/queries/useQueryVerifications";
import { useQueryVerificationMissingData } from "../../../query/onboarding/queries/useQueryVerificationMissingFields";
import {
  FlowStep,
  FlowStepStatus
} from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  onboardingModule: OnboardingModuleCreateAccount,
  verifications?: onboardingApiSpec.operations["getVerifications"]["responses"]["200"]["content"]["application/json"],
  missingDataTax?: onboardingApiSpec.operations["getVerificationMissingData"]["responses"]["200"]["content"]["application/json"],
  missingDataBanking?: onboardingApiSpec.operations["getVerificationMissingData"]["responses"]["200"]["content"]["application/json"]
): FlowStepStatus => {
  const verificationLevel =
    onboardingModule.options?.verificationLevel || "Tax";

  const status =
    verificationLevel === "Tax" ? verifications?.tax : verifications?.banking;
  const missingData =
    verificationLevel === "Tax" ? missingDataTax : missingDataBanking;

  const usPersonStatus =
    verificationLevel === "Tax" && verifications?.profiles?.["US"]?.tax
      ? verifications?.profiles?.["US"]?.tax
      : undefined;

  if (
    status === "Verified" &&
    (usPersonStatus ? usPersonStatus === "Verified" : true)
  ) {
    return "Complete";
  }

  if (
    status === "Pending" ||
    status === "Failed" ||
    usPersonStatus === "Pending" ||
    usPersonStatus === "Failed"
  ) {
    return "Pending";
  }

  const hasNoMissingFields =
    !missingData?.requiredFields || missingData?.requiredFields?.length === 0;

  const hasNoMissingFieldsUsPerson =
    !missingData?.profiles?.["US"]?.requiredFields ||
    missingData?.profiles?.["US"]?.requiredFields.length === 0;

  if (
    (status === "UpdateRequired" || usPersonStatus === "UpdateRequired") &&
    hasNoMissingFields &&
    hasNoMissingFieldsUsPerson
  ) {
    return "Pending";
  }

  return "None";
};

export const getCreateAccountStepConfig = (
  onboardingModule: OnboardingModuleCreateAccount,
  context: CreateAccountContext,
  verifications?: onboardingApiSpec.operations["getVerifications"]["responses"]["200"]["content"]["application/json"],
  missingDataTax?: onboardingApiSpec.operations["getVerificationMissingData"]["responses"]["200"]["content"]["application/json"],
  missingDataBanking?: onboardingApiSpec.operations["getVerificationMissingData"]["responses"]["200"]["content"]["application/json"]
): FlowStep => {
  const slug = "create-account";

  const steps: FlowStep[] = [];

  steps.push({
    title: "Secure account",
    slug: "secure",
    component: SecureAccount
  });

  steps.push({
    title: "Account type",
    slug: "account-type",
    component: AccountType
  });

  if (context?.accountType?.type === "Individual") {
    steps.push({
      title: "Personal information",
      slug: "personal-information",
      component: PersonalInformation
    });

    if (context?.accountType?.isUsPerson) {
      steps.push({
        title: "U.S. person information",
        slug: "international-us-person-information",
        component: InternationalUSPersonInformation
      });
    }
  } else {
    steps.push({
      title: "Business information",
      slug: "business-information",
      component: BusinessInformation
    });

    steps.push({
      title:
        onboardingModule.options?.verificationLevel === "Banking"
          ? "Representative & beneficial owner(s)"
          : "Representative",
      slug: "representative",
      component: Representative
    });
  }

  steps.push({
    title: "Review & submit",
    slug: "review",
    component: Review
  });

  const status = getStatus(
    onboardingModule,
    verifications,
    missingDataTax,
    missingDataBanking
  );

  const taxIdLabel = getTaxIdLabel(
    context?.accountType?.type || "Individual",
    context?.accountType?.country || "US"
  );

  const internationalUSPersonLabel = context?.accountType?.isUsPerson
    ? "and U.S. person SSN"
    : null;

  const taxLabel = [taxIdLabel, internationalUSPersonLabel]
    .filter(Boolean)
    .join(" ");

  return {
    title: onboardingModule.title || "Create account",
    slug,
    steps,
    status,
    result: {
      Complete: {
        title: `Your ${taxLabel} was verified`,
        description: `Your ${taxLabel} was verified. Click continue for the next step.`
      },

      Pending: {
        type: "search",
        title: `Awaiting ${taxLabel} verification`,
        description: `We were not able to verify your ${taxLabel}. We will attempt to verify it again in 24 hours and will reach out if there are any issues. Click continue for the next step.`
      }
    }
  };
};

export const useCreateAccountStep: UseCreateOnboardingStep<
  OnboardingModuleCreateAccount
> = modules => {
  const config = useOnboardingStepQueryConfig(modules, "create_account");
  const queryVerifications = useQueryVerifications(config);
  const queryMissingDataTax = useQueryVerificationMissingData("Tax", config);
  const queryMissingDataBanking = useQueryVerificationMissingData(
    "Banking",
    config
  );

  return (
    onboardingModule: OnboardingModuleCreateAccount,
    contextValue: OnboardingContext
  ) => {
    return buildOnboardingStep({
      getConfig: getCreateAccountStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryVerifications,
        queryMissingDataTax,
        queryMissingDataBanking
      ]
    });
  };
};
